import React, { FC, useState, useEffect } from 'react';
import { graphql, useStaticQuery, Link } from 'gatsby';

import Layout from '../components/Layout';
import SEO from '../components/Seo';
import { Routes } from '../constants/Routes';
import Image from 'gatsby-image';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';

const HOMEPAGE_QUERY = graphql`
  query {
    allContentfulHomepage {
      nodes {
        media {
          fluid(maxWidth: 1920, quality: 85) {
            ...GatsbyContentfulFluid_withWebp
          }
        }
        body {
          json
        }
      }
    }
    jsLogo: file(relativePath: { eq: "logos/js.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 200) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    tvsLogo: file(relativePath: { eq: "logos/tanzvereinigung.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 200) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    danseLogo: file(relativePath: { eq: "logos/dansesuisse.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 200) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    tiwLogo: file(relativePath: { eq: "logos/tanz_in_winterthur.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 200) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
  }
`;

const HomePage: FC = () => {
  const data = useStaticQuery(HOMEPAGE_QUERY);
  const homepage = data.allContentfulHomepage.nodes[0];
  const slides = homepage?.media || [];
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const logos = [
    { key: 'js', fluid: data.jsLogo?.childImageSharp?.fluid },
    { key: 'tvs', fluid: data.tvsLogo?.childImageSharp?.fluid },
    { key: 'danse', fluid: data.danseLogo?.childImageSharp?.fluid },
    { key: 'tiw', fluid: data.tiwLogo?.childImageSharp?.fluid },
  ].filter(l => l.fluid);

  return (
    <Layout location={Routes.HOME}>
      <SEO title="Home" />

      {/* Slideshow Banner */}
      <div className="relative w-full overflow-hidden" style={{ height: '60vh', minHeight: '300px' }}>
        {slides.map((slide, i) => (
          <div
            key={i}
            className="absolute inset-0"
            style={{
              opacity: i === currentSlide ? 1 : 0,
              transition: 'opacity 1s ease-in-out',
            }}
          >
            <Image
              className="w-full h-full"
              style={{ height: '100%' }}
              imgStyle={{ objectFit: 'cover' }}
              fluid={slide.fluid}
              alt=""
            />
          </div>
        ))}
      </div>

      {/* Welcome Text from Contentful */}
      {homepage?.body && (
        <div className="mx-auto px-4 py-12 text-center" style={{ maxWidth: '48rem' }}>
          <div className="text-gray-700 text-lg leading-relaxed markdown-body">
            {documentToReactComponents(homepage.body.json)}
          </div>
        </div>
      )}

      {/* CTA Button */}
      <div className="text-center py-8">
        <Link
          to={Routes.SCHNUPPERLEKTION}
          className="inline-block bg-primary hover:bg-primary-dark text-white text-xl font-bold py-4 px-12 rounded-full tracking-wider uppercase"
          style={{ transition: 'background-color 0.2s ease' }}
        >
          Jetzt Schnuppern!
        </Link>
      </div>

      {/* Partner Logos */}
      <div className="mx-auto px-4 py-8 pb-16" style={{ maxWidth: '56rem' }}>
        <div className="flex items-center justify-center flex-wrap">
          {logos.map(logo => (
            <div key={logo.key} className="mx-4 md:mx-8 mb-4" style={{ width: '7rem' }}>
              <Image fluid={logo.fluid} alt={logo.key} />
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default HomePage;
